import React, {useCallback, useState} from "react";
import {Col, Row} from "reactstrap";
import {Divider} from "@mui/material";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../../core/services/api/api";
import Dialog from "../../../../../../../components/base/dialog";
import Button from "../../../../../../../components/base/button/button";
import {OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import * as Yup from 'yup';
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Form from "../../../../../../../components/base/form";
import {makeRequired, makeValidate} from "mui-rff";

const schema = Yup.object().shape({
    name: Yup.string().trim().nullable().required(ValidateMessages.required),
})

const validate = makeValidate(schema);
const required = makeRequired(schema);

const AddAndEditOperatorGroupModal = ({open, setOpen, onSuccess, group: groupProp}) => {
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Calls api for adding a new operator record, if the result is successful, closes the modal and calls onSuccess
     * with the newly
     * created record as its data.
     */
    const addOperator = useCallback(async (group) => {
        setLoading(true);
        const response = await Api.createOperatorGroup(group);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            onSuccess(response.data);
            setOpen(false);
        }
        setLoading(false);
    }, [isMounted, onSuccess, setOpen]);

    /**
     * Calls api for editing an operator record that already exists. If the api result is successful, closes the
     * modal and calls onSuccess with the edited record as its data.
     */
    const editOperator = useCallback(async (group) => {
        setLoading(true);
        const response = await Api.updateOperatorGroup(group);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            onSuccess(group);
            setOpen(false);
        }
        setLoading(false);
    }, [isMounted, onSuccess, setOpen]);

    /**
     * Submits the changes for the record. (being creating one or editing one)
     */
    const onsubmit = useCallback(async (values) => {
        if (groupProp) {
            await editOperator(values);
        } else {
            await addOperator(values);
        }
    }, [editOperator, addOperator, groupProp])

    const colClass = 'px-3 mb-4';
    return (
        <Dialog
            open={open}
            loading={loading}
            disableEscapeKeyDown={loading}
            disableBackdropClick={loading}
            setOpen={setOpen}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        {groupProp ? 'Edit Group' : 'Add Group'}
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-4 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={onsubmit}
                    validate={validate}
                    initialValues={groupProp}
                    render={({values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={colClass}>
                                        <OutlinedInputForm
                                            label={'Group Name'}
                                            name={'name'}
                                            type={'text'}
                                            required={required.name}
                                            value={values?.name ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-1 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || loading}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {loading ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || loading}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {loading ? 'loading...' : groupProp ? 'Edit' : 'Add'}
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default AddAndEditOperatorGroupModal;
